import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe,
  faGlobeAmericas,
  faDesktop,
  faCloud,
  faPlug,
  faBox,
  faUser,
  faNetworkWired,
  faTimes,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import targetService from '../../services/targetService';
import targetTypesService from '../../services/targetTypesService';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';

const TargetForm = ({ isEditing = false, targetData = null, onClose = null }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [formData, setFormData] = useState({
    project: projectId,
    type: 'ip',
    value: '',
    hostname: '',
    ip: '',
    description: '',
    notes: '',
    metadata: {}
  });

  // Modal mode when onClose is provided
  const isModal = !!onClose;

  useEffect(() => {
    if (isEditing && targetData) {
      setFormData({
        ...targetData,
        hostname: targetData.hostname || '',
        ip: targetData.ip || '',
        metadata: targetData.metadata || {}
      });
    }
  }, [isEditing, targetData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'type') {
      // Reset metadata when type changes
      setFormData(prev => ({
        ...prev,
        [name]: value,
        metadata: {}
      }));
    } else if (name === 'value') {
      // Auto-detect type from value (only when creating)
      const updates = { value };
      if (!isEditing) {
        const detectedType = targetTypesService.detectTargetType(value);
        if (detectedType !== formData.type) {
          updates.type = detectedType;
          updates.metadata = {};
        }
      }
      // Auto-extract hostname from URL for web types
      const effectiveType = updates.type || formData.type;
      if (['webapplication', 'api', 'apiEndpoint'].includes(effectiveType)) {
        try {
          const parsed = new URL(value);
          updates.hostname = parsed.hostname;
        } catch (e) { /* ignore invalid URLs while typing */ }
      }
      setFormData(prev => ({ ...prev, ...updates }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleMetadataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value
      }
    }));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    handleMetadataChange('tags', tags);
  };

  const handleResolveIp = async () => {
    const hostname = formData.hostname || formData.value;
    if (!hostname) {
      toast.error('No hostname to resolve');
      return;
    }
    // Extract hostname from URL if needed
    let resolveTarget = hostname;
    try {
      const parsed = new URL(hostname);
      resolveTarget = parsed.hostname;
    } catch (e) { /* not a URL, use as-is */ }

    setResolving(true);
    try {
      const result = await targetService.resolveHostname(resolveTarget);
      setFormData(prev => ({ ...prev, ip: result.ip }));
      toast.success(`Resolved to ${result.ip}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not resolve hostname');
    } finally {
      setResolving(false);
    }
  };

  const isWebType = ['webapplication', 'api', 'apiEndpoint'].includes(formData.type);

  const getValueLabel = () => {
    if (isWebType) return 'URL *';
    if (formData.type === 'hostname') return 'Hostname *';
    if (formData.type === 'ip') return 'IP Address *';
    return 'Target Value *';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await targetService.updateTarget(targetData._id, formData);
        toast.success('Target updated successfully!');
      } else {
        await targetService.createTarget(formData);
        toast.success('Target created successfully!');
      }
      
      if (isModal && onClose) {
        onClose();
      } else {
        navigate(`/projects/${projectId}?tab=targets`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save target');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(`/projects/${projectId}?tab=targets`);
    }
  };

  const getTargetTypeIcon = (type) => {
    const icons = {
      ip: faGlobe,
      hostname: faDesktop,
      cloudResource: faCloud,
      apiEndpoint: faPlug,
      container: faBox,
      identity: faUser,
      webapplication: faGlobeAmericas,
      network: faNetworkWired
    };
    return icons[type] || faGlobe;
  };

  const currentType = targetTypesService.getTargetType(formData.type);
  const metadataFields = currentType.metadataFields || [];
  const fieldConfig = targetTypesService.getMetadataFieldConfig();

  const formContent = (
    <form onSubmit={handleSubmit}>
      {/* Target Type */}
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Target Type *
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(targetTypesService.targetTypes).map(([type, config]) => (
            <button
              key={type}
              type="button"
              onClick={() => handleChange({ target: { name: 'type', value: type } })}
              className={`p-3 rounded-lg border-2 transition-all ${
                formData.type === type
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <FontAwesomeIcon 
                icon={getTargetTypeIcon(type)} 
                className="text-2xl mb-1 text-primary"
              />
              <div className="text-sm font-medium">{config.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Target Value */}
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="value">
          {getValueLabel()}
        </label>
        <input
          type="text"
          id="value"
          name="value"
          value={formData.value}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          placeholder={currentType.placeholder}
          required
          disabled={loading}
        />
        <p className="text-sm text-gray-500 mt-1">
          Example: {currentType.placeholder}
        </p>
      </div>

      {/* Hostname / IP fields */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {formData.type !== 'hostname' && (
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="hostname">
              Hostname {isWebType && <span className="text-xs text-gray-400 font-normal">(auto-extracted)</span>}
            </label>
            <input
              type="text"
              id="hostname"
              name="hostname"
              value={formData.hostname}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 ${isWebType ? 'bg-gray-50 text-gray-600' : ''}`}
              placeholder="server.example.com"
              disabled={loading}
              readOnly={isWebType}
            />
          </div>
        )}
        {formData.type !== 'ip' && (
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ip">
              IP Address {formData.type === 'hostname' && <span className="text-red-500">*</span>}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="ip"
                name="ip"
                value={formData.ip}
                onChange={handleChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="192.168.1.1"
                required={formData.type === 'hostname'}
                disabled={loading}
              />
              {(formData.hostname || isWebType) && (
                <button
                  type="button"
                  onClick={handleResolveIp}
                  disabled={resolving || loading}
                  className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 text-sm whitespace-nowrap"
                >
                  {resolving ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    t('targets.resolveIp', 'Resolve IP')
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
          Description
        </label>
        <input
          type="text"
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          placeholder="Brief description of this target"
          disabled={loading}
        />
      </div>

      {/* Metadata Fields */}
      {metadataFields.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {metadataFields.map(field => {
              const config = fieldConfig[field];
              if (!config) return null;

              return (
                <div key={field}>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    {config.label}
                  </label>
                  
                  {config.type === 'select' ? (
                    <select
                      value={formData.metadata[field] || ''}
                      onChange={(e) => handleMetadataChange(field, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select {config.label}</option>
                      {config.options.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : config.type === 'tags' ? (
                    <input
                      type="text"
                      value={formData.metadata[field]?.join(', ') || ''}
                      onChange={handleTagsChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder={config.placeholder}
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.metadata[field] || ''}
                      onChange={(e) => handleMetadataChange(field, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder={config.placeholder}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="notes">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          placeholder="Any additional notes about this target"
          disabled={loading}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
          disabled={loading}
        >
          {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />}
          {loading ? 'Saving...' : (isEditing ? 'Update Target' : 'Add Target')}
        </button>
      </div>
    </form>
  );

  // If in modal mode, wrap in a modal container
  if (isModal) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Target' : 'Add New Target'}
            </h1>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>
          {formContent}
        </div>
      </div>
    );
  }

  // Otherwise, render as a regular page
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Target' : 'Add New Target'}
      </h1>

      <div className="bg-white rounded-lg shadow-md p-6">
        {formContent}
      </div>
    </div>
  );
};

export default TargetForm;